import { currencyFormat, separateThousands } from '../../../../../utils/Helper'
import {
  AUTH_PROVIDER_TO_CHANNEL_COLUMN,
  normalizeAuthProvider,
  OVERVIEW_PROVIDER_CONFIG,
  providerForOverviewScopeKey,
  REELS_LIKE_COLUMNS,
  REELS_LIKE_TABLE_KEYS,
  REELS_LIKE_TABLE_KEY_TO_ACTION_TYPE,
  TABLES,
  TABS,
} from './BudgetDetailsPanelConfig'
import { fixed2 } from './BudgetDetailsPanelNumberUtils'
import { isInfluencerVisibleInBudgetScenario } from './influencerBudgetVisibility'

/** Duplicate rows (slotNumber > 1) always start blank and are labeled "<Name> (extra N)", numbered so multiple duplicates are distinguishable. */
export function displayNameForSlot(influencer, slotNumber) {
  const base = influencer?.full_name || ''
  const slot = Number(slotNumber) || 1
  return slot > 1 ? `${base} (extra ${slot - 1})`.trim() : base
}

export function actionsForReelsTableRow(campaignScenario, tableKey, influencerId, slotNumber = 1) {
  const actionType = REELS_LIKE_TABLE_KEY_TO_ACTION_TYPE[tableKey]
  if (!actionType) return []
  const iid = Number(influencerId)
  const slot = Number(slotNumber) || 1
  return (campaignScenario?.actions || [])
    .filter(
      a =>
        a.action_type === actionType &&
        Number(a.influencer_id) === iid &&
        (Number(a.slot_number) || 1) === slot &&
        a.visible_in_budget !== false
    )
    .sort((a, b) => Number(a.id) - Number(b.id))
}

/** Per influencer + action type: sums and blended TKP (overview / action group logic). */
export function aggregateReelsLikeMetrics(actions) {
  let sumReach = 0
  let sumImpression = 0
  let sumPayout = 0
  let sumInfluencerPrice = 0
  let sumAdvertiser = 0
  let sumKsa = 0
  let sumViews = 0
  for (const a of actions) {
    sumReach += Number(a.reach) || 0
    sumImpression += Number(a.impression) || 0
    sumPayout += Number(a.influencer_payout_price) || 0
    const infP = typeof a.influencerPrice === 'number' ? a.influencerPrice : 0
    sumInfluencerPrice += infP
    const adv = typeof a.totalAdvertiserPrice === 'number' ? a.totalAdvertiserPrice : 0
    sumAdvertiser += adv
    const ksa = typeof a.totalKsaPrice === 'number' ? a.totalKsaPrice : 0
    sumKsa += ksa
    const v = a.action_insight?.views ?? a.action_insight?.plays
    if (v != null) sumViews += Number(v) || 0
  }
  const tkpFollower = sumReach > 0 ? (sumAdvertiser / sumReach) * 1000 : 0
  const tkpImp = sumImpression > 0 ? (sumAdvertiser / sumImpression) * 1000 : 0
  return { sumReach, sumImpression, sumPayout, sumInfluencerPrice, sumAdvertiser, sumKsa, sumViews, tkpFollower, tkpImp }
}

/**
 * One row per influencer slot per tab: actions of that type aggregated (budget table group / total social style).
 */
export function reelsLikeRowFromInfluencerActions(influencer, actions, currencySymbol, slotNumber = 1) {
  const sym = currencySymbol || '€'
  const m = aggregateReelsLikeMetrics(actions)
  const viewsStr = m.sumViews > 0 ? separateThousands(m.sumViews) : ''
  const firstAction = actions[0]
  return {
    ...emptyRowFromColumns(REELS_LIKE_COLUMNS),
    influencerId: influencer.id,
    slotNumber,
    authenticationId: firstAction?.authentication_id,
    avatarUrl: firstAuthenticationAvatar(influencer),
    influencer: displayNameForSlot(influencer, slotNumber),
    follower: separateThousands(Number(firstAction?.reach) || 0),
    brutto: m.sumReach > 0 ? separateThousands(m.sumReach) : '0',
    impression: separateThousands(m.sumImpression),
    tkpFollower: m.sumReach > 0 && Number.isFinite(m.tkpFollower) ? currencyFormat(fixed2(m.tkpFollower), sym) : '',
    tkpImpression: m.sumImpression > 0 && Number.isFinite(m.tkpImp) ? currencyFormat(fixed2(m.tkpImp), sym) : '',
    views: viewsStr,
    payout: currencyFormat(fixed2(m.sumPayout), sym),
    preis: currencyFormat(fixed2(m.sumInfluencerPrice), sym),
    ksa: currencyFormat(fixed2(m.sumKsa), sym),
    advertiser: currencyFormat(fixed2(m.sumAdvertiser), sym),
  }
}

export function reelsLikeEmptyRowForInfluencer(influencer, currencySymbol, slotNumber = 1) {
  const sym = currencySymbol || '€'
  return {
    ...emptyRowFromColumns(REELS_LIKE_COLUMNS),
    influencerId: influencer.id,
    slotNumber,
    authenticationId: null,
    avatarUrl: firstAuthenticationAvatar(influencer),
    influencer: displayNameForSlot(influencer, slotNumber),
    follower: '0',
    brutto: '0',
    impression: '0',
    tkpFollower: currencyFormat('0.00', sym),
    tkpImpression: currencyFormat('0.00', sym),
    views: '0',
    payout: currencyFormat('0.00', sym),
    preis: currencyFormat('0.00', sym),
    ksa: currencyFormat('0.00', sym),
    advertiser: currencyFormat('0.00', sym),
  }
}

export function reelsLikeRowsForTableKey(campaignScenario, tableKey, influencers, currencySymbol, campaign, authentications) {
  const actionType = REELS_LIKE_TABLE_KEY_TO_ACTION_TYPE[tableKey]
  if (!actionType) return []
  const byKey = new Map()
  for (const a of campaignScenario?.actions || []) {
    if (a.action_type !== actionType) continue
    if (a.visible_in_budget === false) continue
    const key = slotKey(a.influencer_id, a.slot_number)
    if (!byKey.has(key)) byKey.set(key, [])
    byKey.get(key).push(a)
  }
  const ordered = orderedInfluencerSlotsForScenario(campaignScenario, influencers)
    .filter(({ influencer, slotNumber }) => byKey.has(slotKey(influencer.id, slotNumber)))
    .filter(({ influencer, slotNumber }) =>
      isInfluencerVisibleInBudgetScenario(campaignScenario, influencer, campaign, authentications, slotNumber)
    )
  return ordered.map(({ influencer, slotNumber }) => {
    const actions = byKey.get(slotKey(influencer.id, slotNumber)) || []
    if (!actions.length) return reelsLikeEmptyRowForInfluencer(influencer, currencySymbol, slotNumber)
    return reelsLikeRowFromInfluencerActions(influencer, actions, currencySymbol, slotNumber)
  })
}

export function buildEmptyReelsLikeRowsByKey() {
  return REELS_LIKE_TABLE_KEYS.reduce((acc, k) => {
    acc[k] = []
    return acc
  }, {})
}

export function emptyRowFromColumns(columns) {
  return columns.reduce((acc, col) => {
    acc[col.key] = ''
    return acc
  }, {})
}

export function otherItemActionGroupForInfluencer(campaignScenario, influencerId, slotNumber = 1) {
  const iid = Number(influencerId)
  const slot = Number(slotNumber) || 1
  return (campaignScenario?.campaign_scenario_action_groups || []).find(
    g => g.action_type === 'other_item' && Number(g.influencer_id) === iid && (Number(g.slot_number) || 1) === slot
  )
}

export function otherChannelLabelForInfluencer(campaignScenario, influencerId, slotNumber = 1) {
  return otherItemActionGroupForInfluencer(campaignScenario, influencerId, slotNumber)?.label || ''
}

export function channelNameFieldsFromAuthentications(influencer) {
  const fields = { instagram: '', tiktok: '', youtube: '', linkedin: '', pinterest: '', podcastChannel: '' }
  for (const auth of influencer?.authentications || []) {
    const providerKey = normalizeAuthProvider(auth.provider)
    const columnKey = AUTH_PROVIDER_TO_CHANNEL_COLUMN[providerKey]
    if (!columnKey) continue
    const username = (auth.username || '').trim()
    if (!username) continue
    if (!fields[columnKey]) fields[columnKey] = username
  }
  return fields
}

const OVERVIEW_ANZAHL_ACTION_TYPES = {
  igPost: ['instagram_post'],
  igCarousel: ['instagram_carousel_post'],
  igReel: ['instagram_reel'],
  igStory: ['instagram_story'],
  ttVid: ['tiktok_video'],
  ytVid: ['youtube_video'],
  ytReel: ['youtube_short'],
  liBeitrag: ['linkedin_post'],
  liNewsletter: ['linkedin_newsletter'],
  liVideo: ['linkedin_video'],
  pinPost: ['pinterest_pin'],
  podcast: ['podcast_episode'],
  otherItem: ['other_item'],
}

function overviewScopeDisabledCellsForInfluencer(influencer) {
  const providerKeys = new Set((influencer?.authentications || []).map(auth => normalizeAuthProvider(auth.provider)))
  return Object.keys(OVERVIEW_ANZAHL_ACTION_TYPES).reduce((acc, scopeKey) => {
    const providerKey = providerForOverviewScopeKey(scopeKey)
    if (!providerKey) return acc
    if (!providerKeys.has(providerKey)) acc[scopeKey] = true
    return acc
  }, {})
}

function overviewLockedCellsForInfluencer(influencer) {
  return (influencer?.authentications || []).reduce((acc, auth) => {
    const providerKey = normalizeAuthProvider(auth.provider)
    const channelKey = OVERVIEW_PROVIDER_CONFIG[providerKey]?.channelKey
    if (channelKey && auth.modash_updatable) acc[channelKey] = true
    return acc
  }, {})
}

export function authenticationByProviderForInfluencer(influencer) {
  return (influencer?.authentications || []).reduce((acc, auth) => {
    const providerKey = normalizeAuthProvider(auth.provider)
    if (!providerKey || acc[providerKey]) return acc
    acc[providerKey] = auth
    return acc
  }, {})
}

function influencerScenarioActionsForAnzahl(campaignScenario, influencerId, slotNumber = 1) {
  const id = Number(influencerId)
  const slot = Number(slotNumber) || 1
  return (campaignScenario?.actions || []).filter(a => {
    if (Number(a.influencer_id) !== id) return false
    if ((Number(a.slot_number) || 1) !== slot) return false
    if (a.action_type === 'non_media_item') return false
    if (a.visible_in_budget === false) return false
    return true
  })
}

function anzahlFieldsForInfluencer(campaignScenario, influencerId, slotNumber = 1) {
  const actions = influencerScenarioActionsForAnzahl(campaignScenario, influencerId, slotNumber)
  const count = types =>
    String(actions.filter(a => types.includes(a.action_type)).length)
  return {
    igPost: count(OVERVIEW_ANZAHL_ACTION_TYPES.igPost),
    igCarousel: count(OVERVIEW_ANZAHL_ACTION_TYPES.igCarousel),
    igReel: count(OVERVIEW_ANZAHL_ACTION_TYPES.igReel),
    igStory: count(OVERVIEW_ANZAHL_ACTION_TYPES.igStory),
    ttVid: count(OVERVIEW_ANZAHL_ACTION_TYPES.ttVid),
    ytVid: count(OVERVIEW_ANZAHL_ACTION_TYPES.ytVid),
    ytReel: count(OVERVIEW_ANZAHL_ACTION_TYPES.ytReel),
    liBeitrag: count(OVERVIEW_ANZAHL_ACTION_TYPES.liBeitrag),
    liNewsletter: count(OVERVIEW_ANZAHL_ACTION_TYPES.liNewsletter),
    liVideo: count(OVERVIEW_ANZAHL_ACTION_TYPES.liVideo),
    pinPost: count(OVERVIEW_ANZAHL_ACTION_TYPES.pinPost),
    podcast: count(OVERVIEW_ANZAHL_ACTION_TYPES.podcast),
    otherItem: count(OVERVIEW_ANZAHL_ACTION_TYPES.otherItem),
  }
}

/** Reach/impression/advertiser-price sums across an influencer slot's media actions (Gesamt Media block: BH/BI/BJ equivalents). */
function reachAndImpressionsSumForInfluencer(campaignScenario, influencerId, slotNumber = 1) {
  const actions = influencerScenarioActionsForAnzahl(campaignScenario, influencerId, slotNumber)
  let reach = 0
  let impression = 0
  let advertiserPrice = 0
  for (const a of actions) {
    reach += Number(a.reach) || 0
    impression += Number(a.impression) || 0
    advertiserPrice += typeof a.totalAdvertiserPrice === 'number' ? a.totalAdvertiserPrice : 0
  }
  return { reach, impression, advertiserPrice }
}

export function firstAuthenticationAvatar(influencer) {
  const auths = influencer?.authentications || []
  const authWithAvatar = auths.find(auth => typeof auth?.avatar === 'string' && auth.avatar.trim() !== '')
  return authWithAvatar?.avatar || ''
}

function slotKey(influencerId, slotNumber) {
  return `${Number(influencerId)}:${Number(slotNumber) || 1}`
}

/** Number of content pieces on a row (influencer + slot) - drives the delete-row confirmation copy. */
export function slotContentPieceCount(campaignScenario, influencerId, slotNumber) {
  const id = Number(influencerId)
  const slot = Number(slotNumber) || 1
  return (campaignScenario?.actions || []).filter(
    a => Number(a.influencer_id) === id && (Number(a.slot_number) || 1) === slot
  ).length
}

/** Whether a row (influencer + slot) has any content pieces. */
export function slotHasAnyContentPieces(campaignScenario, influencerId, slotNumber) {
  return slotContentPieceCount(campaignScenario, influencerId, slotNumber) > 0
}

/** First-appearance influencer ordering (action order / campaign.influencers fallback for zero-action influencers). */
function orderedInfluencerObjectsForScenario(campaignScenario, influencers) {
  const list = influencers || []
  const byId = new Map(list.map(inf => [Number(inf.id), inf]))
  const seen = new Set()
  const ordered = []
  for (const action of campaignScenario?.actions || []) {
    const rawId = action.influencer_id
    if (rawId == null || rawId === '') continue
    const id = Number(rawId)
    if (!Number.isFinite(id) || seen.has(id)) continue
    seen.add(id)
    const inf = byId.get(id)
    if (inf) ordered.push(inf)
  }
  for (const inf of list) {
    const id = Number(inf.id)
    if (!Number.isFinite(id) || seen.has(id)) continue
    seen.add(id)
    ordered.push(inf)
  }
  return ordered
}

/** All slot numbers seen for an influencer across groups/actions/custom fields; defaults to [1] when none exist yet. */
function slotNumbersForInfluencer(campaignScenario, influencerId) {
  const iid = Number(influencerId)
  const nums = new Set()
  for (const g of campaignScenario?.campaign_scenario_action_groups || []) {
    if (Number(g.influencer_id) === iid) nums.add(Number(g.slot_number) || 1)
  }
  for (const a of campaignScenario?.actions || []) {
    if (Number(a.influencer_id) === iid) nums.add(Number(a.slot_number) || 1)
  }
  for (const v of campaignScenario?.campaign_custom_field_values || []) {
    if (Number(v.influencer_id) === iid) nums.add(Number(v.slot_number) || 1)
  }
  if (!nums.size) nums.add(1)
  return [...nums].sort((a, b) => a - b)
}

/**
 * Ordered (influencer, slotNumber) row identities: grouped by influencer first (first-appearance
 * order), then slots sorted ascending within each influencer group - so a duplicate "(extra)" row
 * always renders directly below its original, by construction, regardless of creation timing.
 */
export function orderedInfluencerSlotsForScenario(campaignScenario, influencers) {
  const ordered = orderedInfluencerObjectsForScenario(campaignScenario, influencers)
  const result = []
  for (const influencer of ordered) {
    for (const slotNumber of slotNumbersForInfluencer(campaignScenario, influencer.id)) {
      result.push({ influencer, slotNumber })
    }
  }
  return result
}

export function scenarioInfluencerRowBases(campaignScenario, influencers) {
  return orderedInfluencerSlotsForScenario(campaignScenario, influencers).map(({ influencer: inf, slotNumber }) => {
    const { reach, impression, advertiserPrice } = reachAndImpressionsSumForInfluencer(campaignScenario, inf.id, slotNumber)
    const authenticationsByProvider = authenticationByProviderForInfluencer(inf)
    return {
      influencerId: inf.id,
      slotNumber,
      avatarUrl: firstAuthenticationAvatar(inf),
      name: displayNameForSlot(inf, slotNumber),
      authenticationsByProvider,
      disabledCells: overviewScopeDisabledCellsForInfluencer(inf),
      lockedCells: overviewLockedCellsForInfluencer(inf),
      ...channelNameFieldsFromAuthentications(inf),
      otherChannel: otherChannelLabelForInfluencer(campaignScenario, inf.id, slotNumber),
      ...anzahlFieldsForInfluencer(campaignScenario, inf.id, slotNumber),
      totalReach: reach,
      totalImpression: impression,
      totalAdvertiserPrice: advertiserPrice,
    }
  })
}

export function overviewRowFromBase(base, currencySymbol) {
  const sym = currencySymbol || '€'
  const row = {
    ...emptyRowFromColumns(TABLES.overview.columns),
    influencerId: base.influencerId,
    slotNumber: base.slotNumber,
    avatarUrl: base.avatarUrl,
    _authenticationsByProvider: base.authenticationsByProvider || {},
    _disabledCells: base.disabledCells || {},
    _lockedCells: base.lockedCells || {},
    influencer: base.name,
    instagram: base.instagram,
    tiktok: base.tiktok,
    youtube: base.youtube,
    linkedin: base.linkedin,
    pinterest: base.pinterest,
    podcastChannel: base.podcastChannel,
    otherChannel: base.otherChannel,
    igPost: base.igPost,
    igCarousel: base.igCarousel,
    igReel: base.igReel,
    igStory: base.igStory,
    ttVid: base.ttVid,
    ytVid: base.ytVid,
    ytReel: base.ytReel,
    liBeitrag: base.liBeitrag,
    liNewsletter: base.liNewsletter,
    liVideo: base.liVideo,
    pinPost: base.pinPost,
    podcast: base.podcast,
    otherItem: base.otherItem,
  }
  const advertiserPrice = Number(base.totalAdvertiserPrice) || 0
  const reach = Number(base.totalReach) || 0
  const impression = Number(base.totalImpression) || 0
  return {
    ...row,
    // Gesamt Media block: GAPM = Gesamt Advertiser Preis Media (BH); tkpFl/tkpImp = blended TKP (BI/BJ).
    gapm: currencyFormat(fixed2(advertiserPrice), sym),
    tkpFl: reach > 0 ? currencyFormat(fixed2((advertiserPrice / reach) * 1000), sym) : currencyFormat('0.00', sym),
    tkpImp: impression > 0 ? currencyFormat(fixed2((advertiserPrice / impression) * 1000), sym) : currencyFormat('0.00', sym),
  }
}

/** Total social / budget table: KSA, total advertiser price, payout across all actions for the influencer slot. */
export function influencerActionsForOverallKosten(campaignScenario, influencerId, slotNumber = 1) {
  const slot = Number(slotNumber) || 1
  return (campaignScenario?.actions || []).filter(
    a => Number(a.influencer_id) === Number(influencerId) && (Number(a.slot_number) || 1) === slot
  )
}

/** Media actions eligible for buy-out (excludes non-media; respects budget visibility). */
export function influencerMediaActionsForBuyouts(campaignScenario, influencerId, slotNumber = 1) {
  const id = Number(influencerId)
  const slot = Number(slotNumber) || 1
  return (campaignScenario?.actions || [])
    .filter(a => {
      if (Number(a.influencer_id) !== id) return false
      if ((Number(a.slot_number) || 1) !== slot) return false
      if (a.action_type === 'non_media_item') return false
      if (a.visible_in_budget === false) return false
      return true
    })
    .sort((a, b) => Number(a.id) - Number(b.id))
}

function buyoutAdvertiserPriceFromAction(a) {
  if (!a?.buyout) return 0
  const fromGetter = a.buyoutAdvertiserPrice
  if (typeof fromGetter === 'number' && Number.isFinite(fromGetter)) return fromGetter
  const price = Number(a.buyout_price) || 0
  const advMargin = Number(a.advertiser_margin) || 0
  const ksa = a.ksa_tax_enabled ? price * 0.049 : 0
  return price * (1 + advMargin / 100) + ksa
}

function welcheBuyoutsNotesFromActions(actions) {
  const list = actions || []
  const buyoutActions = list.filter(a => a.buyout)
  const source = buyoutActions[0] || list[0]
  return source?.notes ?? ''
}

export function aggregateBuyoutMetrics(actions) {
  const buyoutActions = (actions || []).filter(a => a.buyout)
  const anzahl = buyoutActions.length
  let sumInfluencerBuyout = 0
  let sumAdvertiserBuyout = 0
  const unitPrices = new Set()
  for (const a of buyoutActions) {
    const price = Number(a.buyout_price) || 0
    sumInfluencerBuyout += price
    sumAdvertiserBuyout += buyoutAdvertiserPriceFromAction(a)
    unitPrices.add(price)
  }
  const unitPrice = anzahl > 0 ? sumInfluencerBuyout / anzahl : 0
  const uniformUnitPrice = unitPrices.size === 1 ? [...unitPrices][0] : null
  const welcheBuyouts = welcheBuyoutsNotesFromActions(actions)
  return { anzahl, sumInfluencerBuyout, sumAdvertiserBuyout, unitPrice, uniformUnitPrice, welcheBuyouts }
}

export function buyoutRowFromInfluencer(influencer, campaignScenario, currencySymbol, slotNumber = 1) {
  const actions = influencerMediaActionsForBuyouts(campaignScenario, influencer.id, slotNumber)
  const sym = currencySymbol || '€'
  const m = aggregateBuyoutMetrics(actions)
  const unitDisplay =
    m.anzahl === 0
      ? ''
      : m.uniformUnitPrice != null
        ? currencyFormat(fixed2(m.uniformUnitPrice), sym)
        : currencyFormat(fixed2(m.unitPrice), sym)
  const noMediaActions = actions.length === 0
  return {
    ...emptyRowFromColumns(TABLES.buyouts.columns),
    influencerId: influencer.id,
    slotNumber,
    avatarUrl: firstAuthenticationAvatar(influencer),
    influencer: displayNameForSlot(influencer, slotNumber),
    anzahlBuyout: m.anzahl > 0 ? String(m.anzahl) : '0',
    buyoutUnitPrice: unitDisplay,
    gesamtBuyouts: currencyFormat(fixed2(m.sumInfluencerBuyout), sym),
    buyoutAdvertiser: currencyFormat(fixed2(m.sumAdvertiserBuyout), sym),
    welcheBuyouts: m.welcheBuyouts,
    _noMediaActions: noMediaActions,
    _disabledCells: noMediaActions
      ? { anzahlBuyout: true, buyoutUnitPrice: true, welcheBuyouts: true }
      : {},
  }
}

export function buyoutRowsFromCampaign(campaignScenario, influencers, campaign, authentications) {
  const sym = campaign?.currency_symbol || '€'
  return orderedInfluencerSlotsForScenario(campaignScenario, influencers)
    .filter(({ influencer, slotNumber }) =>
      isInfluencerVisibleInBudgetScenario(campaignScenario, influencer, campaign, authentications, slotNumber)
    )
    .map(({ influencer, slotNumber }) => buyoutRowFromInfluencer(influencer, campaignScenario, sym, slotNumber))
}

export function buyoutFooterTotals(campaignScenario, influencers, campaign, authentications) {
  const ordered = orderedInfluencerSlotsForScenario(campaignScenario, influencers).filter(({ influencer, slotNumber }) =>
    isInfluencerVisibleInBudgetScenario(campaignScenario, influencer, campaign, authentications, slotNumber)
  )
  let sumAnzahl = 0
  let sumInfluencerBuyout = 0
  let sumAdvertiserBuyout = 0
  for (const { influencer, slotNumber } of ordered) {
    const actions = influencerMediaActionsForBuyouts(campaignScenario, influencer.id, slotNumber)
    const m = aggregateBuyoutMetrics(actions)
    sumAnzahl += m.anzahl
    sumInfluencerBuyout += m.sumInfluencerBuyout
    sumAdvertiserBuyout += m.sumAdvertiserBuyout
  }
  return { sumAnzahl, sumInfluencerBuyout, sumAdvertiserBuyout }
}

/**
 * Media-only KSA for one action (Action#ksaPrice / Action.js ksaPrice getter) - excludes buyout KSA.
 * This is the piece Excel's "Marge in EUR" subtracts back out, making media KSA neutral to margin;
 * Buyout/Sonstiges KSA is deliberately NOT subtracted here, so it stays as extra margin (matches Excel).
 */
function mediaOnlyKsaFromAction(a) {
  if (!a) return 0
  const fromGetter = a.ksaPrice
  if (typeof fromGetter === 'number' && Number.isFinite(fromGetter)) return fromGetter
  const payout = Number(a.influencer_payout_price) || 0
  return a.ksa_tax_enabled ? payout * KSA_RATE : 0
}

/** Sums the per-action figures (Overall Kosten) needed for Kosten Advertiser / KSA gesamt / Payout / Marge. */
function actionTotalsForOverallKosten(actions) {
  let advertiserTotal = 0
  let ksaTotal = 0
  let mediaOnlyKsaTotal = 0
  let payoutRawTotal = 0
  for (const a of actions) {
    advertiserTotal += typeof a.totalAdvertiserPrice === 'number' ? a.totalAdvertiserPrice : 0
    ksaTotal += typeof a.totalKsaPrice === 'number' ? a.totalKsaPrice : 0
    mediaOnlyKsaTotal += mediaOnlyKsaFromAction(a)
    payoutRawTotal += Number(a.influencer_payout_price) || 0
    if (a.buyout) payoutRawTotal += Number(a.buyout_price) || 0
  }
  return { advertiserTotal, ksaTotal, mediaOnlyKsaTotal, payoutRawTotal }
}

export function overallKostenRowFromInfluencer(influencer, campaignScenario, currencySymbol, campaign, slotNumber = 1) {
  const actions = influencerActionsForOverallKosten(campaignScenario, influencer.id, slotNumber)
  const sym = currencySymbol || '€'
  const actionTotals = actionTotalsForOverallKosten(actions)
  const customFieldTotals = customFieldContributionsForInfluencer(campaignScenario, influencer.id, campaign, slotNumber)

  const kostenAdvertiserTotal = actionTotals.advertiserTotal + customFieldTotals.advertiserTotal
  const ksaGesamtTotal = actionTotals.ksaTotal + customFieldTotals.ksaTotal
  const payoutTotal = actionTotals.payoutRawTotal + customFieldTotals.payoutRawTotal
  const margeEurTotal = kostenAdvertiserTotal - actionTotals.mediaOnlyKsaTotal - payoutTotal

  const a0 = actions[0]
  const marginStr = v => (v != null && v !== '' ? String(v) : '')
  return {
    ...emptyRowFromColumns(TABLES['overall-kosten'].columns),
    influencerId: influencer.id,
    slotNumber,
    avatarUrl: firstAuthenticationAvatar(influencer),
    influencer: displayNameForSlot(influencer, slotNumber),
    ksaGesamt: currencyFormat(fixed2(ksaGesamtTotal), sym),
    kostenAdvertiser: currencyFormat(fixed2(kostenAdvertiserTotal), sym),
    payout: currencyFormat(fixed2(payoutTotal), sym),
    kommentar: '',
    margeEur: currencyFormat(fixed2(margeEurTotal), sym),
    margeBuyouts: a0 ? marginStr(a0.influencer_payout_margin) : '',
    margeMedia: a0 ? marginStr(a0.advertiser_margin) : '',
  }
}

/** Overall Kosten table: totals for visible rows (same logic as per-row sums). */
export function overallKostenFooterTotals(campaignScenario, influencers, campaign, authentications) {
  const ordered = orderedInfluencerSlotsForScenario(campaignScenario, influencers).filter(({ influencer, slotNumber }) =>
    isInfluencerVisibleInBudgetScenario(campaignScenario, influencer, campaign, authentications, slotNumber)
  )
  let sumKsa = 0
  let sumAdvertiser = 0
  let sumPayout = 0
  let sumMargeEur = 0
  for (const { influencer, slotNumber } of ordered) {
    const actions = influencerActionsForOverallKosten(campaignScenario, influencer.id, slotNumber)
    const actionTotals = actionTotalsForOverallKosten(actions)
    const customFieldTotals = customFieldContributionsForInfluencer(campaignScenario, influencer.id, campaign, slotNumber)

    const rowAdvertiser = actionTotals.advertiserTotal + customFieldTotals.advertiserTotal
    const rowKsa = actionTotals.ksaTotal + customFieldTotals.ksaTotal
    const rowPayout = actionTotals.payoutRawTotal + customFieldTotals.payoutRawTotal

    sumKsa += rowKsa
    sumAdvertiser += rowAdvertiser
    sumPayout += rowPayout
    sumMargeEur += rowAdvertiser - actionTotals.mediaOnlyKsaTotal - rowPayout
  }
  return { sumKsa, sumAdvertiser, sumPayout, sumMargeEur }
}

export function normalizedCustomFields(campaign) {
  const fields = Array.isArray(campaign?.custom_fields) ? campaign.custom_fields : []
  return fields
    .filter(f => !!(f?.label || '').trim())
    .map((field, index) => ({
      id: field.id ?? `cf-${index}`,
      label: (field.label || '').trim(),
      fieldType: field.field_type || 'text_field',
      advertiserFeeEnabled: field.advertiser_fee_enabled !== false,
      ksaEnabled: field.ksa_enabled !== false,
    }))
}

export function customFieldColumnKey(field) {
  return `customField_${field.id}`
}

export function isCostItemField(field) {
  return (field?.fieldType || field?.field_type) === 'cost_item'
}

/** Cost-item fields render as 4 sub-columns instead of one free-value column. */
export function customFieldCostItemKeys(field) {
  const base = customFieldColumnKey(field)
  return { quantity: `${base}_qty`, unitPrice: `${base}_price`, gesamt: `${base}_gesamt`, advertiser: `${base}_advertiser` }
}

const ADVERTISER_FEE_RATE = 0.20
const KSA_RATE = 0.049

/** quantity * unit_price, +20% fee and/or +KSA per the field's own toggles. Mirrors CampaignCustomFieldValue#advertiser_price. */
export function costItemAmountsFromRecord(field, record) {
  const quantity = Number(record?.quantity) || 0
  const unitPrice = Number(record?.unit_price) || 0
  const raw = quantity * unitPrice
  const fee = field.advertiserFeeEnabled ? raw * ADVERTISER_FEE_RATE : 0
  const ksa = field.ksaEnabled ? raw * KSA_RATE : 0
  return { raw, fee, ksa, advertiserPrice: raw + fee + ksa }
}

/** Display string for a campaign_custom_field_value row by field type (note/numeric only; cost_item uses customFieldCostItemKeys). */
export function displayValueFromCustomFieldRecord(field, record) {
  if (!record) return ''
  const type = field.fieldType || field.field_type
  if (type === 'numeric') {
    const n = record.numeric_value
    if (n == null || n === '') return ''
    return String(n)
  }
  return record.text_value ?? ''
}

export function findCustomFieldValueForInfluencer(campaignScenario, influencerId, campaignCustomFieldId, slotNumber = 1) {
  const sid = Number(campaignScenario?.id)
  const iid = Number(influencerId)
  const fid = Number(campaignCustomFieldId)
  const slot = Number(slotNumber) || 1
  if (!Number.isFinite(fid)) return undefined
  return (campaignScenario?.campaign_custom_field_values || []).find(
    v =>
      Number(v.influencer_id) === iid &&
      Number(v.campaign_custom_field_id) === fid &&
      (Number(v.slot_number) || 1) === slot &&
      (Number.isFinite(sid) ? Number(v.campaign_scenario_id) === sid : true)
  )
}

export function upsertCampaignCustomFieldValueInScenario(campaignScenario, record) {
  if (!campaignScenario || record == null) return
  const list = [...(campaignScenario.campaign_custom_field_values || [])]
  const rid = record.id
  if (rid == null) return
  const idx = list.findIndex(v => Number(v.id) === Number(rid))
  if (idx >= 0) list[idx] = { ...list[idx], ...record }
  else list.push(record)
  campaignScenario.campaign_custom_field_values = list
}

/**
 * Sonstiges contribution to Overall Kosten: plain "Number" fields add their raw value straight into
 * Kosten Advertiser (unchanged legacy behavior). "Cost item" fields go through the quantity*unit_price
 * + optional 20% fee + optional KSA pipeline (mirrors CampaignCustomFieldValue#advertiser_price) and also
 * feed KSA Abgabe gesamt and Payout Influencer.
 */
function customFieldContributionsForInfluencer(campaignScenario, influencerId, campaign, slotNumber = 1) {
  const fields = normalizedCustomFields(campaign)
  const result = { advertiserTotal: 0, ksaTotal: 0, payoutRawTotal: 0 }
  if (!fields.length) return result

  for (const field of fields) {
    const record = findCustomFieldValueForInfluencer(campaignScenario, influencerId, field.id, slotNumber)
    if (isCostItemField(field)) {
      const amounts = costItemAmountsFromRecord(field, record)
      result.advertiserTotal += amounts.advertiserPrice
      result.ksaTotal += amounts.ksa
      result.payoutRawTotal += amounts.raw
    } else if ((field.fieldType || field.field_type) === 'numeric') {
      const n = Number(record?.numeric_value)
      if (Number.isFinite(n)) result.advertiserTotal += n
    }
  }
  return result
}

export function customFieldsTableConfigFromCampaign(campaign) {
  const fields = normalizedCustomFields(campaign)
  const plainFields = fields.filter(f => !isCostItemField(f))
  const costItemFields = fields.filter(isCostItemField)
  const plainFieldKeys = plainFields.map(customFieldColumnKey)
  return {
    groups: [
      { label: 'Influencer', keys: ['influencer'] },
      ...(plainFieldKeys.length ? [{ label: 'Sonstiges', keys: plainFieldKeys }] : []),
      ...costItemFields.map(field => {
        const keys = customFieldCostItemKeys(field)
        return { label: field.label, keys: [keys.quantity, keys.unitPrice, keys.gesamt, keys.advertiser] }
      }),
    ],
    columns: [
      { key: 'influencer', label: '' },
      ...plainFields.map(field => ({
        key: customFieldColumnKey(field),
        label: field.label,
        input: true,
      })),
      ...costItemFields.flatMap(field => {
        const keys = customFieldCostItemKeys(field)
        return [
          { key: keys.quantity, label: 'Anzahl', input: true },
          { key: keys.unitPrice, label: 'Preis', input: true },
          { key: keys.gesamt, label: 'Gesamt', dark: true },
          { key: keys.advertiser, label: 'Advertiser Preis inkl. KSA', dark: true },
        ]
      }),
    ],
    rows: [],
  }
}

/** Populates one budget-table row for a Sonstiges influencer slot: plain fields get one value column, cost_item fields get 4 (qty/price/gesamt/advertiser). */
export function customFieldBudgetRowForInfluencer(influencer, campaignScenario, fields, currencySymbol, slotNumber = 1) {
  const sym = currencySymbol || '€'
  const row = {
    influencerId: influencer.id,
    slotNumber,
    avatarUrl: firstAuthenticationAvatar(influencer),
    influencer: displayNameForSlot(influencer, slotNumber),
  }
  for (const field of fields) {
    const record = findCustomFieldValueForInfluencer(campaignScenario, influencer.id, field.id, slotNumber)
    if (isCostItemField(field)) {
      const keys = customFieldCostItemKeys(field)
      const amounts = costItemAmountsFromRecord(field, record)
      row[keys.quantity] = record?.quantity != null ? String(record.quantity) : ''
      row[keys.unitPrice] = record?.unit_price != null ? String(record.unit_price) : ''
      row[keys.gesamt] = currencyFormat(fixed2(amounts.raw), sym)
      row[keys.advertiser] = currencyFormat(fixed2(amounts.advertiserPrice), sym)
    } else {
      row[customFieldColumnKey(field)] = displayValueFromCustomFieldRecord(field, record)
    }
  }
  return row
}

export function customFieldBudgetRowsFromCampaign(campaignScenario, influencers, campaign, authentications) {
  const fields = normalizedCustomFields(campaign)
  const ordered = orderedInfluencerSlotsForScenario(campaignScenario, influencers).filter(({ influencer, slotNumber }) =>
    isInfluencerVisibleInBudgetScenario(campaignScenario, influencer, campaign, authentications, slotNumber)
  )
  return ordered.map(({ influencer, slotNumber }) =>
    customFieldBudgetRowForInfluencer(influencer, campaignScenario, fields, campaign?.currency_symbol, slotNumber)
  )
}

/** Overview counts / reach: include actions hidden from budget so hidden rows still show numbers. */
function influencerScenarioActionsForAnzahlIgnoringVisibility(campaignScenario, influencerId, slotNumber = 1) {
  const id = Number(influencerId)
  const slot = Number(slotNumber) || 1
  return (campaignScenario?.actions || []).filter(a => {
    if (Number(a.influencer_id) !== id) return false
    if ((Number(a.slot_number) || 1) !== slot) return false
    if (a.action_type === 'non_media_item') return false
    return true
  })
}

function anzahlFieldsForInfluencerIgnoringVisibility(campaignScenario, influencerId, slotNumber = 1) {
  const actions = influencerScenarioActionsForAnzahlIgnoringVisibility(campaignScenario, influencerId, slotNumber)
  const count = types => String(actions.filter(a => types.includes(a.action_type)).length)
  return {
    igPost: count(OVERVIEW_ANZAHL_ACTION_TYPES.igPost),
    igCarousel: count(OVERVIEW_ANZAHL_ACTION_TYPES.igCarousel),
    igReel: count(OVERVIEW_ANZAHL_ACTION_TYPES.igReel),
    igStory: count(OVERVIEW_ANZAHL_ACTION_TYPES.igStory),
    ttVid: count(OVERVIEW_ANZAHL_ACTION_TYPES.ttVid),
    ytVid: count(OVERVIEW_ANZAHL_ACTION_TYPES.ytVid),
    ytReel: count(OVERVIEW_ANZAHL_ACTION_TYPES.ytReel),
    liBeitrag: count(OVERVIEW_ANZAHL_ACTION_TYPES.liBeitrag),
    liNewsletter: count(OVERVIEW_ANZAHL_ACTION_TYPES.liNewsletter),
    liVideo: count(OVERVIEW_ANZAHL_ACTION_TYPES.liVideo),
    pinPost: count(OVERVIEW_ANZAHL_ACTION_TYPES.pinPost),
    podcast: count(OVERVIEW_ANZAHL_ACTION_TYPES.podcast),
    otherItem: count(OVERVIEW_ANZAHL_ACTION_TYPES.otherItem),
  }
}

function reachAndImpressionsSumForInfluencerIgnoringVisibility(campaignScenario, influencerId, slotNumber = 1) {
  const actions = influencerScenarioActionsForAnzahlIgnoringVisibility(campaignScenario, influencerId, slotNumber)
  let reach = 0
  let impression = 0
  let advertiserPrice = 0
  for (const a of actions) {
    reach += Number(a.reach) || 0
    impression += Number(a.impression) || 0
    advertiserPrice += typeof a.totalAdvertiserPrice === 'number' ? a.totalAdvertiserPrice : 0
  }
  return { reach, impression, advertiserPrice }
}

function scenarioInfluencerRowBasesForHiddenSlots(campaignScenario, hiddenSlots) {
  return hiddenSlots.map(({ influencer: inf, slotNumber }) => {
    const { reach, impression, advertiserPrice } = reachAndImpressionsSumForInfluencerIgnoringVisibility(
      campaignScenario,
      inf.id,
      slotNumber
    )
    return {
      influencerId: inf.id,
      slotNumber,
      avatarUrl: firstAuthenticationAvatar(inf),
      name: displayNameForSlot(inf, slotNumber),
      ...channelNameFieldsFromAuthentications(inf),
      otherChannel: otherChannelLabelForInfluencer(campaignScenario, inf.id, slotNumber),
      ...anzahlFieldsForInfluencerIgnoringVisibility(campaignScenario, inf.id, slotNumber),
      totalReach: reach,
      totalImpression: impression,
      totalAdvertiserPrice: advertiserPrice,
    }
  })
}

function reelsLikeRowsForHiddenSlots(campaignScenario, tableKey, hiddenSlots, currencySymbol) {
  const actionType = REELS_LIKE_TABLE_KEY_TO_ACTION_TYPE[tableKey]
  if (!actionType) return []
  const byKey = new Map()
  for (const a of campaignScenario?.actions || []) {
    if (a.action_type !== actionType) continue
    const key = slotKey(a.influencer_id, a.slot_number)
    if (!byKey.has(key)) byKey.set(key, [])
    byKey.get(key).push(a)
  }
  return hiddenSlots
    .filter(({ influencer, slotNumber }) => byKey.has(slotKey(influencer.id, slotNumber)))
    .map(({ influencer, slotNumber }) => {
      const actions = byKey.get(slotKey(influencer.id, slotNumber)) || []
      if (!actions.length) return reelsLikeEmptyRowForInfluencer(influencer, currencySymbol, slotNumber)
      return reelsLikeRowFromInfluencerActions(influencer, actions, currencySymbol, slotNumber)
    })
}

function customFieldBudgetRowsForHiddenSlots(campaignScenario, hiddenSlots, campaign) {
  const fields = normalizedCustomFields(campaign)
  return hiddenSlots.map(({ influencer, slotNumber }) =>
    customFieldBudgetRowForInfluencer(influencer, campaignScenario, fields, campaign?.currency_symbol, slotNumber)
  )
}

/** Rows for influencer slots that are hidden from the main budget table (drives the "Show Hidden List" panel). */
export function hiddenInfluencerSlotsForScenario(campaignScenario, influencers, campaign, authentications) {
  return orderedInfluencerSlotsForScenario(campaignScenario, influencers).filter(
    ({ influencer, slotNumber }) => !isInfluencerVisibleInBudgetScenario(campaignScenario, influencer, campaign, authentications, slotNumber)
  )
}

export function budgetTableRowsForHiddenInfluencers({
  activeTabTableKey,
  campaignScenario,
  campaign,
  hiddenSlotsList,
  currencySymbol,
}) {
  if (!campaignScenario || !hiddenSlotsList?.length) return []
  const sym = currencySymbol || '€'

  if (activeTabTableKey === 'overview') {
    return scenarioInfluencerRowBasesForHiddenSlots(campaignScenario, hiddenSlotsList).map(base => overviewRowFromBase(base, sym))
  }
  if (REELS_LIKE_TABLE_KEYS.includes(activeTabTableKey)) {
    return reelsLikeRowsForHiddenSlots(campaignScenario, activeTabTableKey, hiddenSlotsList, sym)
  }
  if (activeTabTableKey === 'overall-kosten') {
    return hiddenSlotsList.map(({ influencer, slotNumber }) =>
      overallKostenRowFromInfluencer(influencer, campaignScenario, sym, campaign, slotNumber)
    )
  }
  if (activeTabTableKey === 'buyouts') {
    return hiddenSlotsList.map(({ influencer, slotNumber }) => buyoutRowFromInfluencer(influencer, campaignScenario, sym, slotNumber))
  }
  if (activeTabTableKey === 'custom') {
    return customFieldBudgetRowsForHiddenSlots(campaignScenario, hiddenSlotsList, campaign)
  }
  return []
}

export function buildDefaultVisibleColumnsByTab(forcedHiddenColumns, customFieldsTableConfig = null) {
  const initial = {}
  TABS.forEach(tab => {
    const table = tab.tableKey === 'custom' ? (customFieldsTableConfig || TABLES.custom) : TABLES[tab.tableKey]
    if (!table) return
    initial[tab.key] = table.columns.reduce(
      (acc, col) => ({ ...acc, [col.key]: !forcedHiddenColumns[col.key] }),
      {}
    )
  })
  return initial
}

export function mergeSavedBudgetColumnVisibility(saved, forcedHiddenColumns, customFieldsTableConfig = null) {
  const defaults = buildDefaultVisibleColumnsByTab(forcedHiddenColumns, customFieldsTableConfig)
  const savedHash = saved && typeof saved === 'object' ? saved : {}
  const out = {}
  Object.keys(defaults).forEach(tabKey => {
    out[tabKey] = {}
    const savedTab =
      tabKey === 'custom'
        ? savedHash.custom ?? savedHash.sonstigen
        : savedHash[tabKey]
    Object.keys(defaults[tabKey]).forEach(colKey => {
      const stored = savedTab?.[colKey]
      out[tabKey][colKey] = defaults[tabKey][colKey] && stored !== false
    })
  })
  return out
}

export function scenarioCustomFieldValuesFingerprint(values) {
  if (!values?.length) return ''
  return values
    .map(v =>
      [v.id, v.influencer_id, v.slot_number, v.campaign_custom_field_id, v.campaign_scenario_id, v.text_value, v.numeric_value].join(':')
    )
    .join('|')
}

/** Sync key without JSON.stringify - safer with MobX / BigInt / circular refs. */
export function scenarioActionsFingerprint(actions) {
  if (!actions?.length) return ''
  return actions
    .map(a => {
      const ins = a.action_insight
      return [
        a.id ?? '',
        a.influencer_id ?? '',
        a.slot_number ?? '',
        a.action_type ?? '',
        a.visible_in_budget ?? '',
        a.impression ?? '',
        a.influencer_payout_price ?? '',
        a.reach ?? '',
        a.advertiser_margin ?? '',
        a.influencer_payout_margin ?? '',
        a.buyout ? '1' : '0',
        a.buyout_price ?? '',
        a.notes ?? '',
        a.ksa_tax_enabled ? '1' : '0',
        ins?.views ?? '',
        ins?.plays ?? '',
      ].join(':')
    })
    .join('|')
}

export function scenarioActionGroupsFingerprint(groups) {
  if (!groups?.length) return ''
  return [...groups]
    .sort((a, b) => Number(a.id) - Number(b.id))
    .map(g => `${g.id}:${g.influencer_id ?? ''}:${g.slot_number ?? ''}:${g.visible_in_budget ? 1 : 0}:${g.label ?? ''}:${g.reach ?? ''}`)
    .join('|')
}
